import { Injectable } from "@angular/core";
import { IAudioState } from "../domain/audio-state";
import { BehaviorSubject, Observable } from "rxjs";
import { IAudioPlayList } from "../domain/audio-play-list";
import { StringUtils } from "../utils/string.utils";

@Injectable({
  providedIn: 'root'
})
export class AudioService {
  private _audio = new Audio();
  private _audioPlayList = new Audio();
  private audioContext = new (window.AudioContext || (window as any).webkitAudioContext)({
        sampleRate: 24000,
        latencyHint: 'interactive'
    }
  );

  private state: IAudioState = {
    playing: false,
    readableCurrentTime: '00:00',
    readableDuration: '00:00',
    duration: 0,
    currentTime: 0,
    ended: false,
      triggerEvent: 'initial'
  };
  private stateChange = new BehaviorSubject<IAudioState>(this.state);
  private _playList: IAudioPlayList = {
    listOfBytes: [],
    playingIndex: 0,
    isLoaded: false,
    listOfBase64Strings: []
  }
  private _isPlayingListInProgress = false;

  constructor() {
    this.listenToAudioEvents();
  }

  public async playAudioBytes(audioBytes: Uint8Array) {
    const blob= new Blob([audioBytes as Uint8Array<ArrayBuffer>], {type : 'audio/wav'});
    const blobWithHeader = await this.appendWavHeader(blob);
    this.playBlod(blobWithHeader);
  }

  public playBlod(blob: Blob) {
    const blobUrl = URL.createObjectURL(blob);
    if(!this._audio.paused) {
      this._audio.pause()
    }
    this._audio.src = blobUrl;
    console.log('blobUrl', blobUrl)
    this._audio.play();
  }

  private getWavHeader(pcmDataLength: number): ArrayBuffer {
    const wavHeader = new ArrayBuffer(44);
    const view = new DataView(wavHeader);
    const sampleRate = 24000;
    const numChannels = 1;      // Mono
    const bitsPerSample = 16;   // PCM16
    const blockAlign = (numChannels * bitsPerSample) / 8;
    const byteRate = sampleRate * blockAlign;
    const dataSize = pcmDataLength;
    const chunkSize = 36 + dataSize;
    this.writeString(view, 0, 'RIFF');
    view.setUint32(4, chunkSize, true);
    this.writeString(view, 8, 'WAVE');
    this.writeString(view, 12, 'fmt ');
    view.setUint32(16, 16, true);  // Subchunk1Size (16 for PCM)
    view.setUint16(20, 1, true);   // AudioFormat (1 = PCM)
    view.setUint16(22, numChannels, true);
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, byteRate, true);
    view.setUint16(32, blockAlign, true);
    view.setUint16(34, bitsPerSample, true);
    this.writeString(view, 36, 'data');
    view.setUint32(40, dataSize, true);
    return wavHeader;
  }

  private writeString(view: DataView, offset: number, string: string) {
    for (let i = 0; i < string.length; i++) {
      view.setUint8(offset + i, string.charCodeAt(i));
    }
  }

  private async appendWavHeader(rawPcmBlob: Blob): Promise<Blob> {
    const pcmArrayBuffer = await rawPcmBlob.arrayBuffer();
    const wavHeader = this.getWavHeader(pcmArrayBuffer.byteLength);
    const combinedBuffer = new Uint8Array(wavHeader.byteLength + pcmArrayBuffer.byteLength);
    combinedBuffer.set(new Uint8Array(wavHeader), 0);
    combinedBuffer.set(new Uint8Array(pcmArrayBuffer), wavHeader.byteLength);
    return new Blob([combinedBuffer], { type: 'audio/wav' });
  }

  public getState(): Observable<IAudioState> {
    return this.stateChange.asObservable();
  }

  public loadAudio(url: string) {
    this._audio.src = url;
    this._audio.load();
  }

  public play() {
    this._audio.play();
  }

  public pause() {
    this._audio.pause();
  }

public stop() {
    this._audio.pause();
    this._audio.currentTime = 0;
  }

  private listenToAudioEvents() {
    const events = ['play', 'playing', 'pause', 'timeupdate', 'ended', 'loadedmetadata'];
    events.forEach(event => {
      this._audio.addEventListener(event, () => this.updateStateEvents(event));
    });
  }

  private updateStateEvents(event: string) {
      this.state.ended = false;

      switch (event) {
      case 'play':
      case 'playing':
        this.state.playing = true;
        break;
      case 'pause':
        this.state.playing = false;
        break;
      case 'ended':
        this.state.playing = false;
        this.state.ended = true;
        break;
      case 'timeupdate':
        this.state.currentTime = this._audio.currentTime;
        break;
      case 'loadedmetadata':
        this.state.duration = this._audio.duration;
        break;
    }
    this.stateChange.next({
        ...this.state,
        triggerEvent: event
    });
  }

  public async playPlayListOfBytes(bytes: Uint8Array) {
    this._playList.listOfBytes.push(bytes);
    if(this._isPlayingListInProgress) {
      return;
    }
    this._isPlayingListInProgress = true;
    do {
      while(this._playList.playingIndex + 1 > this._playList.listOfBytes.length) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      const currentBytes = this._playList.listOfBytes[this._playList.playingIndex ];
      const blob= new Blob([currentBytes as Uint8Array<ArrayBuffer>], {type : 'audio/wav'});
      const blobWithHeader = await this.appendWavHeader(blob);
      const blobUrl = URL.createObjectURL(blobWithHeader);
      await this.playBlobUrl(blobUrl);
      this._playList.playingIndex = this._playList.playingIndex + 1;
    } while(!this._playList.isLoaded || (this._playList.playingIndex < this._playList.listOfBytes.length));
    this._isPlayingListInProgress = false;
  }

  public endPlayList(){
    this._playList.isLoaded = true;
  }

  private playBlobUrl(blobUrl: string): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        this._audioPlayList.src = blobUrl;
        this._audioPlayList.onended = () => resolve();
        this._audioPlayList.play();
      } catch (err) {
        console.error('Error decoding audio chunk', err);
        reject(err);
      }
    });
  }

  public async playPlayListOfStrings(base64String: string) {
    this._playList.listOfBase64Strings.push(base64String);
    if(this._isPlayingListInProgress) {
      return;
    }
    this._isPlayingListInProgress = true;
    do {
      while(this._playList.playingIndex + 1 > this._playList.listOfBase64Strings.length) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      const currentString = this._playList.listOfBase64Strings[this._playList.playingIndex ];
      const audioBytes = StringUtils.base64ToBytes(currentString);
      const blob= new Blob([audioBytes as Uint8Array<ArrayBuffer>], {type : 'audio/wav'});
      const blobWithHeader = await this.appendWavHeader(blob);
      const stringWithHeader = await StringUtils.convertBlobToBase64(blobWithHeader);
      const arrayBuffer = this.base64ToArrayBuffer(stringWithHeader);
      try {
        await this.playAudioBuffer(arrayBuffer);
      } catch(err) {
        console.error('Error playing audio chunk', err);
      }
      this._playList.playingIndex = this._playList.playingIndex + 1;
    } while(!this._playList.isLoaded || (this._playList.playingIndex < this._playList.listOfBase64Strings.length));
    this._isPlayingListInProgress = false;
  }

  private base64ToArrayBuffer(base64: string): ArrayBuffer {
    const binaryString = atob(base64);
    const bytes = Uint8Array.from(binaryString, (char) => char.charCodeAt(0));
    return bytes.buffer;
  }

  private playAudioBuffer(arrayBuffer: ArrayBuffer): Promise<void> {
    return new Promise((resolve, reject) => {
      this.audioContext.decodeAudioData(arrayBuffer, (buffer) => {
        const source = this.audioContext.createBufferSource();
        source.buffer = buffer;
        source.connect(this.audioContext.destination);
        source.onended = () => resolve();
        source.start(0);
      }, (err) => {
        console.error('Error decoding audio chunk', err);
        reject(err);
      });
    });
  }
}
